import type { FactoringCompany } from '@store/carriers/carrierDetails/types';
import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';

import { CHANGE_TRACKING_SOURCES } from '../../utils/changeTrackingRegistry';
import { createFormChangeTracker } from '../../utils/createFormChangeTracker';
import { findChangedForms } from '../../utils/findChangedForms';

const IGNORED_FIELDS = new Set([
  'carriers',
  'comments',
  'createdDate',
  'isCarrierExist',
  'modifiedDate',
  'numberOfCarriers',
  'operationType',
  'primaryContact',
]);

const DETAILS_FIELDS = new Set([
  'achAccount',
  'achRouting',
  'billingTerms',
  'name',
  'remitTo',
  'status',
]);

const PHYSICAL_ADDRESS_FIELDS = new Set([
  'physicalAddress1',
  'physicalAddress2',
  'physicalCity',
  'physicalCountryCode',
  'physicalState',
  'physicalZip',
]);

const MAILING_ADDRESS_FIELDS = new Set([
  'mailingAddress1',
  'mailingAddress2',
  'mailingCity',
  'mailingCountryCode',
  'mailingState',
  'mailingZip',
]);

const PORTAL_LOGIN_FIELDS = new Set(['password', 'userName']);

const isEmptyFormValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const areFactoringCompanyScalarValuesEqual = (
  before: unknown,
  after: unknown,
): boolean => {
  if (Object.is(before, after)) return true;
  if (isEmptyFormValue(before) && isEmptyFormValue(after)) return true;

  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }

  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }

  return false;
};

const getFactoringCompanyDetailsForm = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource => {
  const [field] = getPropertyPath(path);

  if (field === 'contacts') {
    return CHANGE_TRACKING_SOURCES.factoringCompanyDetails
      .factoringCompanyDetailsContact;
  }

  if (MAILING_ADDRESS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.factoringCompanyDetails
      .factoringCompanyDetailsMailingAddress;
  }

  if (PHYSICAL_ADDRESS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.factoringCompanyDetails
      .factoringCompanyDetailsPhysicalAddress;
  }

  if (PORTAL_LOGIN_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.factoringCompanyDetails
      .factoringCompanyDetailsPortalLogin;
  }

  if (DETAILS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.factoringCompanyDetails
      .factoringCompanyDetailsDetails;
  }

  return CHANGE_TRACKING_SOURCES.factoringCompanyDetails
    .factoringCompanyDetailsDetails;
};

const findFactoringCompanyDetailsChangedForms = (
  before: FactoringCompany,
  after: FactoringCompany,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    ignoredFields: IGNORED_FIELDS,
    areScalarValuesEqual: areFactoringCompanyScalarValuesEqual,
    getForm: (change) => getFactoringCompanyDetailsForm(change.path),
  });

const factoringCompanyDetailsFormChangeTracker = createFormChangeTracker<
  FactoringCompany,
  ChangeTrackingSource
>({
  getIdentity: (factoringCompany) => factoringCompany.id || undefined,
  findChangedForms: findFactoringCompanyDetailsChangedForms,
});

export const setFactoringCompanyDetailsChangeBaseline =
  factoringCompanyDetailsFormChangeTracker.setBaseline;
export const clearFactoringCompanyDetailsChangeBaseline =
  factoringCompanyDetailsFormChangeTracker.clearBaseline;
export const prepareFactoringCompanyDetailsChangeSummary =
  factoringCompanyDetailsFormChangeTracker.prepare;
